export type Vehicle = {
  ano: number;
  caminho: string;
  id: number;
  idModelo: number;
  idMarca: number;
  imagem: string;
  nome: string;
  nomeMarca: string;
  nomeModelo: string;
  nomeVersao: string;
  /**
   * As duas notas da avaliação do AutoPapo, de 0 a 5. Nulas quando a versão não tem
   * avaliação da equipe: a média vem de um SELECT sem linhas, e o que chega é null.
   */
  notaCustoBeneficio: number | null;
  notaGeral: number | null;
  numeroDeVersoes: number;
  preco: number;
  urlDetalhes: string;
  /**
   * A prévia do carro (/ranking/carro/{marca}/{versao}/): fotos por ângulo, as dez notas da
   * avaliação e os números da versão, com a ficha técnica como passo seguinte. É para cá que
   * o card leva — `urlDetalhes` é a ficha, e continua vindo por ser a URL canônica do veículo.
   */
  urlPrevia: string;
  novo: boolean;
  cambio: string;
  /**
   * A imagem é a foto do modelo, e não daquela versão (a versão não tem foto própria). O
   * card avisa isso em vermelho — é temporário, para achar as versões sem foto.
   */
  imagemDoModelo: boolean;
};
export type VehicleResult = {
  data: Vehicle[];
  total: number;
};
export type BasicData = {
  id: number;
  nome: string;
};
export type Marca = BasicData & {
  imagem: string;
  /** Marca popular (Marca.Comum no cadastro): abre a lista, antes das demais. */
  principal: boolean;
};
export type SelectData = {
  marcas: Marca[];
  opcionais: BasicData[];
  combustiveis: BasicData[];
  cambios: BasicData[];
  modelos: BasicData[];
};
export type Ordem = {
  id?: number;
  campo?: string;
  ordem?: string;
  label: string;
};

export const instanceUrl = process.env.REACT_APP_API_URL;

/**
 * Topo da escala das notas do ranking: 5 estrelas, a escala do formulário de avaliação.
 * Do lado do C# o mesmo valor vive em EscalaDaNota.Maxima (RankingViewModel.cs): os dois
 * precisam bater, porque a API trata "máximo igual ao topo" como "sem limite".
 *
 * Era 10, quando a Nota AutoPapo da listagem era o Versao.notaGeral do iCarros. Agora as
 * duas notas saem da NotaCarro, então a listagem fala a mesma língua da LP.
 */
export const NOTA_MAXIMA = 5;

export const campos: Ordem[] = [
  { campo: "NotaGeral", ordem: "desc", label: "Nota AutoPapo" },
  { campo: "NotaCustoBeneficio", ordem: "desc", label: "Nota Custo Benefício" },
  { campo: "Preco", ordem: "desc", label: "Maior Preço" },
  { campo: "Preco", ordem: "asc", label: "Menor Preço" },
  { campo: "AnoModelo", ordem: "desc", label: "Ano mais novo" },
];

export type Range = {
  min: number;
  max: number;
};

/**
 * Uma faixa aberta nas pontas, usada pelos filtros de ficha técnica. Diferente de Range, os
 * dois lados são opcionais: "porta-malas grande" é só um mínimo, "até 5 lugares" é só um
 * máximo, e "médio" é os dois. Lado ausente significa sem limite daquele lado.
 */
export type Limite = {
  minimo?: number;
  maximo?: number;
};

export const getVehicles = (path: string) => {
  const parsedPath = `/search/?${path}`;
  const abortController = new AbortController();
  const promise: Promise<VehicleResult> = get(parsedPath, abortController);
  return { cancel: () => abortController.abort(), promise };
};
export const get = async (path: string, abortController?: AbortController) => {
  const url = `${instanceUrl}/api/ranking${path}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "access-control-allow-origin": "*",
        "Content-type": "application/json; charset=UTF-8",
      },
      signal: abortController?.signal,
    });
    const data = await response.json();
    return data;
  } catch (err: any) {
    if (err.name === "AbortError") {
      return;
    }
    throw err;
  }
};

export const getMarcas = async () => {
  const marcas: Marca[] = await get("/marcas");
  return marcas;
};

export const getModelos = async (marca: number) => {
  const modelos: BasicData[] = await get(`/modelos?idMarca=${marca}`);
  return modelos;
};
export const getVersoes = async (modelo: number) => {
  const versoes: BasicData[] = await get(`/versoes?idModelo=${modelo}`);
  return versoes;
};

export const getOpcionais = async () => {
  const opcionais: BasicData[] = await get(`/opcionais`);
  return opcionais;
};
export const getCambios = async () => {
  const cambios: BasicData[] = await get(`/cambios`);
  return cambios;
};

export const getCombustiveis = async () => {
  const combustiveis: BasicData[] = await get(`/combustiveis`);
  return combustiveis;
};

/**
 * Situação do usuário na newsletter. "pendente" = pediu pra receber mas ainda
 * não clicou no link de confirmação (duplo opt-in do MailChimp).
 */
export type SituacaoNewsletter = "inscrito" | "pendente" | "nao_inscrito";

export type PerfilLogado = {
  idContaAutoPapo: string;
  nome: string;
  sobrenome: string;
  nomeCompleto: string;
  email: string;
  foto: string;
  isAdmin: boolean;
  primeiroAcesso: string | null;
  ultimoAcesso: string | null;
  newsletter: SituacaoNewsletter;
};

export const getPerfilLogado = async (): Promise<PerfilLogado | null> => {
  const url = `${instanceUrl}/api/Perfil/ObterPerfilLogado`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
      credentials: "include",
    });
    if (!response.ok) return null;
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text) as PerfilLogado;
  } catch {
    return null;
  }
};

export const getSelectData = async (brandId: number) => {
  const [marcas, opcionais, cambios, combustiveis, modelos] = await Promise.all(
    [
      getMarcas(),
      getOpcionais(),
      getCambios(),
      getCombustiveis(),
      getModelos(brandId),
    ]
  );
  const selectData: SelectData = {
    marcas,
    opcionais,
    cambios,
    combustiveis,
    modelos,
  };
  return selectData;
};
